import React from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import {
  EuleGlyph,
  BieneGlyph,
  KatzeGlyph,
  HundGlyph,
} from "@/components/FokustypGlyphs";
import { ArrowRight, ArrowLeft, Lock } from "lucide-react";
import FreeWebinarBanner from "@/components/dashboard/FreeWebinarBanner";
// V1.C (Launch-Consolidation, 30.08.2026): ProLibraryBonusSection entfernt,
// weil sie „Bald verfügbar"-Kacheln für unreleased Module (M-22/M-23) zeigt.
// Post-Launch wieder aktivieren, sobald Assets live sind.

// Sprint M5.36 (29.07.2026) — Issue 13: Karten-Reihenfolge Katze/Eule/Biene/Hund
// (verbindliche 4er-Matrix, siehe lib/focusRoles.js).
const CARDS = [
  { slug: "katze", name: "Katze", fokusfrage: "Will ich das?", Glyph: KatzeGlyph },
  { slug: "eule", name: "Eule", fokusfrage: "Macht das Sinn?", Glyph: EuleGlyph },
  { slug: "biene", name: "Biene", fokusfrage: "Wie geht das?", Glyph: BieneGlyph },
  { slug: "hund", name: "Hund", fokusfrage: "Für wen ist das wichtig?", Glyph: HundGlyph },
];

export default function DashboardFokustyp() {
  const navigate = useNavigate();

  return (
    <DashboardLayout>
      {({ data }) => {
        const accessible = new Set(data.steckbriefe_zugaenglich || []);
        const primarySlug = data.user?.primary_fokustyp;
        return (
          <div className="max-w-5xl mx-auto px-6 lg:px-8 py-12 space-y-8" data-testid="steckbrief-overview-page">
            {/* Master-Prompt v2.6.7 §G — Backlink "← Dashboard" als Text-Link */}
            <button
              onClick={() => navigate("/cockpit")}
              className="inline-flex items-center gap-1 text-sm font-semibold text-[var(--fts-text-muted)] hover:text-[var(--fts-primary-bright)]"
              data-testid="steckbrief-overview-back"
            >
              <ArrowLeft className="w-4 h-4" /> Mitgliederbereich
            </button>

            <header>
              <p className="text-xs font-bold uppercase tracking-widest text-[var(--fts-primary-bright)]" data-testid="steckbrief-overview-eyebrow">
                Fokustypen-Lexikon
              </p>
              <h1 className="mt-2 text-3xl sm:text-4xl font-extrabold tracking-tight text-[var(--fts-text-primary)]">
                Die 4 Fokustypen
              </h1>
              <p className="mt-3 text-base text-[var(--fts-text-muted)] max-w-2xl">
                Jeder Fokustyp filtert deine Botschaft anders. Hier siehst du, wie sie ticken — und worauf du achten kannst.
              </p>
            </header>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="steckbrief-grid">
              {CARDS.map(({ slug, name, fokusfrage, Glyph }) => {
                const unlocked = accessible.has(slug);
                const isPrimary = primarySlug === slug;
                // Master-Prompt v2.6.7 §F — Steckbrief-Kacheln einfärben:
                // Primärer Fokustyp grün, andere drei orange.
                const tileStyle = isPrimary
                  ? {
                      backgroundColor: "var(--fts-tile-primary-bg)",
                      borderColor: "var(--fts-tile-primary-border)",
                    }
                  : {
                      backgroundColor: "var(--fts-tile-other-bg)",
                      borderColor: "var(--fts-tile-other-border)",
                    };
                return (
                  <button
                    key={slug}
                    type="button"
                    onClick={() => navigate(`/dashboard/fokustyp/${slug}`)}
                    data-testid={`steckbrief-card-${slug}`}
                    style={tileStyle}
                    className="relative text-left rounded-2xl border-2 p-7 hover:-translate-y-0.5 transition-all duration-200 shadow-sm"
                  >
                    {!unlocked && (
                      <div
                        className="absolute top-4 right-4 inline-flex items-center gap-1 text-xs font-bold text-[var(--fts-text-muted)] bg-white/80 rounded-full px-2.5 py-1"
                        data-testid={`steckbrief-lock-${slug}`}
                      >
                        <Lock className="w-3 h-3" />
                        Pro Solo
                      </div>
                    )}
                    {isPrimary && (
                      <div
                        className="absolute top-4 left-4 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest text-white bg-[var(--fts-tile-primary-border)] rounded-full px-2.5 py-1"
                        data-testid={`steckbrief-primary-badge-${slug}`}
                      >
                        Dein Fokustyp
                      </div>
                    )}
                    <div className="flex flex-col items-center text-center">
                      <Glyph size={64} />
                      <h2 className="mt-4 text-2xl font-extrabold tracking-tight text-[var(--fts-text-primary)]">
                        {name}
                      </h2>
                      <p className="mt-1 text-sm italic text-[var(--fts-text-muted)]">
                        „{fokusfrage}“
                      </p>
                      <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-[var(--fts-text-primary)]">
                        Steckbrief ansehen <ArrowRight className="w-4 h-4" />
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* V1.C (Launch-Consolidation, 30.08.2026) — ProLibraryBonusSection
                entfernt. Enthielt „Bald verfügbar"-Kacheln fuer M-22/M-23,
                die als unreleased Module hidden sein sollen. */}
          </div>
        );
      }}
    </DashboardLayout>
  );
}
